import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  isPending?: boolean;
  /** Additionally disables just the confirm button (Cancel stays enabled) — e.g. while a pre-confirm check is loading or failed. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  /** When set, renders a text input. If requiredValue is provided, the confirm button
   *  is disabled until the input matches it (matches Flutter's delete dialog behavior). */
  confirmInput?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    requiredValue?: string;
  };
  /** When set, renders a textarea (e.g. rejection reason). Set `required` to gate the confirm button on a non-empty (trimmed) value. */
  textarea?: {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    maxLength?: number;
  };
  /** When set, renders a single checkbox (e.g. "also reset strikes"). Never gates the confirm button. */
  checkbox?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
  };
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  destructive = false,
  isPending = false,
  confirmDisabled = false,
  onConfirm,
  confirmInput,
  textarea,
  checkbox,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {/* asChild: block-content descriptions (e.g. a deal list) are invalid nested inside the default <p>. */}
          <DialogDescription asChild>
            <div className="min-w-0">{description}</div>
          </DialogDescription>
        </DialogHeader>
        {textarea && (
          <div className="space-y-1.5">
            {textarea.label && <p className="text-sm font-medium">{textarea.label}</p>}
            <Textarea
              value={textarea.value}
              onChange={(e) => textarea.onChange(e.target.value)}
              placeholder={textarea.placeholder}
              maxLength={textarea.maxLength}
              rows={3}
            />
            {typeof textarea.maxLength === "number" && (
              <p
                className={`text-xs text-right ${
                  textarea.value.length > textarea.maxLength
                    ? "text-destructive"
                    : "text-muted-foreground"
                }`}
              >
                {textarea.value.length}/{textarea.maxLength}
              </p>
            )}
          </div>
        )}
        {confirmInput && (
          <Input
            value={confirmInput.value}
            onChange={(e) => confirmInput.onChange(e.target.value)}
            placeholder={confirmInput.placeholder}
            autoFocus
          />
        )}
        {checkbox && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="confirm-dialog-checkbox"
              checked={checkbox.checked}
              onCheckedChange={(checked) => checkbox.onChange(checked === true)}
            />
            <Label htmlFor="confirm-dialog-checkbox" className="text-sm font-normal">
              {checkbox.label}
            </Label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={
              isPending ||
              confirmDisabled ||
              (confirmInput?.requiredValue !== undefined &&
                confirmInput.value !== confirmInput.requiredValue) ||
              (textarea?.required === true && textarea.value.trim() === "")
            }
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
